import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["untilField", "summary"]
  static values = {
    summaryTemplate: String,
    summaryTemplateOne: String
  }

  connect () {
    this.boundUpdateSummary = this.updateSummary.bind(this)
    this.boundOnFormChange = this.onFormChange.bind(this)
    this.boundOnModalShown = () => {
      this.bindFlatpickrHooks()
      this.updateSummary()
    }

    this.form?.addEventListener("change", this.boundOnFormChange, true)
    this.form?.addEventListener("input", this.boundOnFormChange, true)

    this.modalElement = this.element.closest(".modal")
    this.modalElement?.addEventListener("shown.bs.modal", this.boundOnModalShown)

    this.toggle()
    this.bindFlatpickrHooks()
    this.updateSummary()
  }

  disconnect () {
    this.form?.removeEventListener("change", this.boundOnFormChange, true)
    this.form?.removeEventListener("input", this.boundOnFormChange, true)
    this.modalElement?.removeEventListener("shown.bs.modal", this.boundOnModalShown)
  }

  onFormChange () {
    this.bindFlatpickrHooks()
    this.updateSummary()
  }

  toggle () {
    if (!this.hasUntilFieldTarget) return

    this.untilFieldTarget.classList.toggle("d-none", !this.recurringChecked)
    this.bindFlatpickrHooks()
    this.updateSummary()
  }

  bindFlatpickrHooks () {
    ;["session[date]", "session[recurrence_until]"].forEach((name) => {
      const input = this.findInput(name)
      const flatpickrInstance = input?._flatpickr
      if (!flatpickrInstance || flatpickrInstance._sessionRecurrenceHooked) return

      flatpickrInstance._sessionRecurrenceHooked = true
      const existingOnChange = [].concat(flatpickrInstance.config.onChange || [])
      flatpickrInstance.set("onChange", [...existingOnChange, this.boundUpdateSummary])
    })
  }

  updateSummary () {
    if (!this.hasSummaryTarget) return

    if (!this.recurringChecked) {
      this.hideSummary()
      return
    }

    const startDate = this.parseDate(this.inputValue("session[date]"))
    const endDate = this.parseDate(this.inputValue("session[recurrence_until]"))

    if (!startDate || !endDate || endDate < this.addWeeks(startDate, 1)) {
      this.hideSummary()
      return
    }

    const count = this.countWeeklySessions(startDate, endDate)
    if (count < 1) {
      this.hideSummary()
      return
    }

    const template = count === 1 && this.hasSummaryTemplateOneValue
      ? this.summaryTemplateOneValue
      : this.summaryTemplateValue

    if (!template) {
      this.hideSummary()
      return
    }

    this.summaryTarget.textContent = this.interpolate(template, {
      count: String(count),
      start_date: this.formatDate(startDate),
      end_date: this.formatDate(endDate)
    })
    this.summaryTarget.classList.remove("d-none")
  }

  hideSummary () {
    this.summaryTarget.textContent = ""
    this.summaryTarget.classList.add("d-none")
  }

  countWeeklySessions (startDate, endDate) {
    let count = 0
    let current = startDate

    while (current <= endDate) {
      count += 1
      current = this.addWeeks(current, 1)
    }

    return count
  }

  addWeeks (date, weeks) {
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    next.setDate(next.getDate() + (weeks * 7))
    return next
  }

  parseDate (value) {
    if (!value) return null

    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return null

    const [, year, month, day] = match
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  formatDate (date) {
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    })
  }

  interpolate (template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`%{${key}}`, value),
      template
    )
  }

  inputValue (name) {
    const input = this.findInput(name)
    if (!input) return null

    const flatpickrInstance = input._flatpickr
    if (flatpickrInstance?.selectedDates?.length) {
      return flatpickrInstance.formatDate(flatpickrInstance.selectedDates[0], "Y-m-d")
    }

    return input.value
  }

  findInput (name) {
    const form = this.form
    if (!form) return null

    return Array.from(form.elements).find((element) => element.name === name) || null
  }

  get recurringChecked () {
    const checkbox = Array.from(this.form?.elements || []).find(
      (element) => element.name === "session[recurring]" && element.type === "checkbox"
    )

    return Boolean(checkbox?.checked)
  }

  get form () {
    return this.element.closest("form")
  }
}
